// @flow

import React from 'react';
import {Helmet} from 'react-helmet';

import {useSiteMetadata} from 'hooks/useSiteMetadata';
import type {PageMetaType, PathType} from 'types';
import {slashifyUrl} from 'utils/urlFormatters';

type Props = {
  path: PathType,
  pageMeta?: PageMetaType,
};

const PageMeta = (props: Props): React$Element<React$FragmentType> => {
  const {siteUrl, siteName, siteDescription, sitePublisher} = useSiteMetadata();
  const {path, pageMeta} = props;

  const canonical = slashifyUrl(`${siteUrl}${path}`);

  return (
    <React.Fragment>
      {/* Defaults. These will be used unless overriden by the definition below, or another Helmet include from a layout/view etc */}
      <Helmet defaultTitle={siteName} titleTemplate={`%s - ${siteName}`}>
        <html lang="en" />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover"
        />

        <link rel="canonical" href={canonical} />
        <link rel="icon" href="/favicon.png" />

        <meta name="description" content={siteDescription} />
        <meta name="Copyright" content={`Copyright (c) ${sitePublisher}`} />

        <meta property="og:locale" content="en" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${siteUrl}/images/banner.png`} />

        <meta name="twitter:creator" content="Hanno" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={siteName} />
        <meta name="twitter:description" content={siteDescription} />
        <meta
          name="twitter:image"
          content={`${siteUrl}/images/banner-twitter.png`}
        />

        <meta name="theme-color" content="#ea4d47" />
        <meta name="msapplication-navbutton-color" content="#ea4d47" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#ea4d47" />
      </Helmet>

      {/* By adding dynamic properties here, we are able to override any defaults set above */}
      <Helmet>
        {pageMeta && pageMeta.title && <title>{pageMeta.title}</title>}
        {pageMeta && pageMeta.description && (
          <meta name="description" content={pageMeta.description} />
        )}
      </Helmet>

      <noscript>
        <link rel="stylesheet" href="https://use.typekit.net/oxm1upu.css" />
      </noscript>
    </React.Fragment>
  );
};

export default PageMeta;
